import { useRef, useState } from 'react';
import type { FormConfig } from '../../config/types';
import { ApiError } from '../../utils/api-error';
import ModerationDialog from './ModerationDialog';
import { downloadModerationReport } from './report';
import type { ModerationPeriod, ModerationRow } from './service';

export default function ModerationReportDownload({
  rows,
  period,
  year,
  config,
  token,
  disabled,
  onGenerating,
}: {
  rows: ModerationRow[];
  period: ModerationPeriod;
  year: string;
  config: FormConfig;
  token: () => Promise<string>;
  disabled: boolean;
  onGenerating: (value: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reportYear, setReportYear] = useState(year);
  const [grade, setGrade] = useState('');
  const [error, setError] = useState('');
  const busy = useRef(false);
  const button = useRef<HTMLButtonElement>(null);
  const years = [
    ...new Set(rows.filter((row) => row.period === period).map((row) => row.reportYear)),
  ]
    .filter(Boolean)
    .sort()
    .reverse();
  const matches = rows.filter(
    (row) =>
      row.period === period &&
      row.reportYear === reportYear &&
      (!grade || row.grade.replace(/\s/g, '') === grade),
  );
  const download = async () => {
    if (busy.current || !matches.length) return;
    busy.current = true;
    setOpen(false);
    setError('');
    onGenerating(true);
    try {
      await downloadModerationReport(await token(), config, matches);
      requestAnimationFrame(() => button.current?.focus());
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.detail || cause.message
          : cause instanceof Error
            ? cause.message
            : 'Unable to download moderation report.',
      );
      setOpen(true);
    } finally {
      busy.current = false;
      onGenerating(false);
    }
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        className="govuk-button govuk-button--secondary"
        disabled={disabled || busy.current}
        onClick={() => {
          setReportYear(years.includes(year) ? year : years[0] || '');
          setGrade('');
          setError('');
          setOpen(true);
        }}
      >
        Download Moderation Report
      </button>
      {open && (
        <ModerationDialog title="Download Moderation Report" onClose={() => setOpen(false)}>
          <form
            className="scs-moderation-dialog__content"
            onSubmit={(event) => {
              event.preventDefault();
              void download();
            }}
          >
            <p className="govuk-body">
              {period === 'Q2' ? 'Mid-year' : 'End of Year'} moderation responses.
            </p>
            {error && (
              <p className="govuk-error-message" role="alert">
                {error}
              </p>
            )}
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-report-year">
                Reporting period
              </label>
              <select
                className="govuk-select"
                id="moderation-report-year"
                value={reportYear}
                onChange={(event) => setReportYear(event.target.value)}
                disabled={!years.length}
              >
                {years.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-report-grade">
                Substantive Grade
              </label>
              <select
                className="govuk-select"
                id="moderation-report-grade"
                value={grade}
                onChange={(event) => setGrade(event.target.value)}
              >
                <option value="">All Grades</option>
                {['SCS1', 'SCS2', 'SCS3', 'SCS4'].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            {!matches.length && (
              <p className="govuk-body" role="status">
                No responses match these filters.
              </p>
            )}
            <button type="submit" className="govuk-button" disabled={!matches.length}>
              Download
            </button>{' '}
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </form>
        </ModerationDialog>
      )}
    </>
  );
}
